/**
 * Compact, agent-friendly serialization of the current page DOM, in the
 * agent-browser snapshot style: one line per element, accessible name first,
 * then the targeting data attributes. See
 * services/agent-core/docs/specs/interactive-onboarding.md.
 *
 * Large pages exceed any line budget, so the tree is emitted with a
 * fair-share budget: every page region receives a slice of the budget
 * proportional to its content, and truncated regions end with an explicit
 * marker instead of silently disappearing.
 */

type SerializeLimits = {
  maxDepth: number;
  maxNodes: number;
  maxChars: number;
  maxNameLength: number;
};

const DEFAULT_LIMITS: SerializeLimits = {
  maxDepth: 16,
  maxNodes: 500,
  maxChars: 30 * 1024,
  maxNameLength: 60,
};

const SKIP_TAGS = new Set([
  'script',
  'style',
  'noscript',
  'template',
  'meta',
  'link',
  'head',
  'br',
]);

const INTERACTIVE_TAGS = new Set([
  'button',
  'a',
  'input',
  'select',
  'textarea',
  'summary',
  'label',
]);

const INTERACTIVE_ROLES = new Set([
  'button',
  'link',
  'tab',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'checkbox',
  'radio',
  'switch',
  'searchbox',
  'textbox',
  'combobox',
  'slider',
  'option',
  'treeitem',
  'dialog',
]);

const STRUCTURAL_TAGS = new Set([
  'header',
  'nav',
  'main',
  'aside',
  'form',
  'dialog',
  'section',
]);

type TaggedName = {
  tag: string;
  name: string;
  /** All data-* attributes, in DOM order — the targeting anchors. */
  data: Array<[string, string]>;
  role: string;
};

/**
 * data-* attributes emitted by UI primitives (Radix & friends) on almost
 * every element they render. They are exposed on emitted lines like any
 * other data-* attribute, but do not by themselves make an element an
 * anchor worth emitting.
 */
const NOISY_DATA_ATTRS = new Set([
  'data-state',
  'data-orientation',
  'data-side',
  'data-align',
  'data-highlighted',
]);

/** One emittable element in the intermediate tree. */
type DomNode = {
  line: string;
  children: DomNode[];
  /** Interesting nodes in this subtree — the basis for budget shares. */
  weight: number;
};

const readableName = (element: Element, isContainer: boolean): string => {
  const explicit =
    element.getAttribute('aria-label') ||
    element.getAttribute('placeholder') ||
    element.getAttribute('alt') ||
    element.getAttribute('title');
  if (explicit) {
    return explicit.trim();
  }
  // Containers must not inherit a name from their descendants: a header
  // holding every button would read as one giant label.
  if (isContainer) {
    return '';
  }
  // Value-bearing controls (textarea, select, rich-text editors) must not
  // fall back to textContent: that is user-entered content — the composer's
  // own draft, a chosen option, an edited document paragraph — and would be
  // serialized into the snapshot and shipped to the model on every send.
  const tag = element.tagName.toLowerCase();
  if (
    tag === 'textarea' ||
    tag === 'select' ||
    (element as HTMLElement).isContentEditable
  ) {
    return '';
  }
  const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text;
};

const isHidden = (element: Element): boolean => {
  if (
    element.hasAttribute('hidden') ||
    element.getAttribute('aria-hidden') === 'true'
  ) {
    return true;
  }
  // checkVisibility() is wrong for this purpose: display:contents wrappers
  // (common layout containers) generate no box and would be dropped with
  // their whole subtree. Test the computed box directly instead.
  const style = window.getComputedStyle(element);
  return style.display === 'none' || style.visibility === 'hidden';
};

const describe = (element: Element): TaggedName | null => {
  const tag = element.tagName.toLowerCase();
  const data: Array<[string, string]> = [];
  for (const attr of element.attributes) {
    if (!attr.name.startsWith('data-')) {
      continue;
    }
    // Collapse whitespace runs to '-' so the emitted anchor is directly
    // usable in a CSS attribute selector ("app header" → app-header).
    data.push([attr.name, attr.value.trim().replace(/\s+/g, '-')]);
  }
  const role = element.getAttribute('role') ?? '';
  // Interactive elements keep their text name even when they wrap spans;
  // non-interactive containers must not inherit a name from descendants (a
  // header holding every button would otherwise read as one giant label).
  const isInteractive =
    INTERACTIVE_TAGS.has(tag) || INTERACTIVE_ROLES.has(role);
  const isContainer = element.children.length > 0 && !isInteractive;
  const name = readableName(element, isContainer);
  return {
    tag,
    name,
    data,
    role,
  };
};

const isAnchorData = (data: Array<[string, string]>): boolean =>
  data.some(([name]) => !NOISY_DATA_ATTRS.has(name));

const isInteresting = (element: Element, described: TaggedName): boolean =>
  isAnchorData(described.data) ||
  INTERACTIVE_TAGS.has(described.tag) ||
  INTERACTIVE_ROLES.has(described.role) ||
  (described.tag === 'img' && Boolean(element.getAttribute('alt')));

const formatLine = (
  described: TaggedName,
  depth: number,
  limits: SerializeLimits,
): string => {
  const parts: string[] = [`- ${described.tag}`];
  const name = described.name.slice(0, limits.maxNameLength);
  if (name) {
    parts.push(`"${name}"`);
  }
  if (described.role) {
    parts.push(`[role=${described.role}]`);
  }
  for (const [attr, value] of described.data) {
    const normalized = value.slice(0, limits.maxNameLength);
    parts.push(normalized ? `[${attr}=${normalized}]` : `[${attr}]`);
  }
  return `${'  '.repeat(depth)}${parts.join(' ')}`;
};

/**
 * Pass 1: build the intermediate tree of emittable elements. Non-emittable
 * wrappers are flattened away: their emittable descendants attach to the
 * nearest emittable ancestor, so skipped chains do not deepen the tree.
 */
// Traversal budget: the output budget (maxNodes) bounds what we emit, but
// without a traversal cap pass 1 still walks the entire DOM with a
// getComputedStyle per element, which can block the send handler for
// hundreds of milliseconds on very large pages.
const MAX_DOM_VISITS = 3000;

const buildTree = (root: Element, limits: SerializeLimits): DomNode[] => {
  let visited = 0;
  const visit = (element: Element, depth: number): DomNode[] => {
    if (depth > limits.maxDepth) {
      return [];
    }
    visited += 1;
    if (visited > MAX_DOM_VISITS) {
      return [];
    }
    if (SKIP_TAGS.has(element.tagName.toLowerCase()) || isHidden(element)) {
      return [];
    }

    const described = describe(element);
    if (!described) {
      return [];
    }
    const emit =
      isInteresting(element, described) || STRUCTURAL_TAGS.has(described.tag);

    const children = Array.from(element.children).flatMap((child) =>
      visit(child, emit ? depth + 1 : depth),
    );

    if (!emit) {
      return children;
    }
    const line = formatLine(described, depth, limits);
    return [
      {
        line,
        children,
        weight: 1 + children.reduce((sum, child) => sum + child.weight, 0),
      },
    ];
  };

  return visit(root, 0);
};

const skippedMarker = (count: number, depth: number): string =>
  `${'  '.repeat(depth)}… (+${count} more skipped)`;

/**
 * Pass 2: emit the tree under a node budget. Siblings split the budget
 * proportionally to their subtree weight; small sibling groups (distinct
 * page regions) additionally get a guaranteed minimum so one heavy region
 * (e.g. a long findings list) cannot starve the rest of the page. Large
 * homogeneous lists fall back to pure proportional sampling with an
 * explicit skipped marker.
 */
const MIN_REGION_SHARE = 2;

const allocateShares = (nodes: DomNode[], budget: number): number[] => {
  const totalWeight = nodes.reduce((sum, node) => sum + node.weight, 0);
  const shares = nodes.map((node) =>
    Math.floor((budget * node.weight) / totalWeight),
  );
  const reserveMode = nodes.length <= Math.max(2, Math.floor(budget / 4));
  if (reserveMode && budget >= nodes.length * MIN_REGION_SHARE) {
    for (let i = 0; i < nodes.length; i++) {
      shares[i] = Math.max(
        shares[i],
        Math.min(nodes[i].weight, MIN_REGION_SHARE),
      );
    }
  }
  // Hand out any rounding slack to the most under-allocated siblings.
  let left = budget - shares.reduce((sum, share) => sum + share, 0);
  while (left > 0) {
    let best = -1;
    let bestDeficit = 0;
    for (let i = 0; i < nodes.length; i++) {
      const deficit = nodes[i].weight - shares[i];
      if (deficit > bestDeficit) {
        bestDeficit = deficit;
        best = i;
      }
    }
    if (best === -1) {
      break;
    }
    shares[best] += 1;
    left -= 1;
  }
  return shares;
};

const renderTree = (
  nodes: DomNode[],
  budget: number,
  depth: number,
  out: string[],
): number => {
  if (budget <= 0 || nodes.length === 0) {
    return 0;
  }
  const shares = allocateShares(nodes, budget);
  let used = 0;
  let skipped = 0;

  for (let i = 0; i < nodes.length; i++) {
    if (shares[i] < 1) {
      skipped += nodes[i].weight;
      continue;
    }
    out.push(nodes[i].line);
    used += 1;
    used += renderTree(nodes[i].children, shares[i] - 1, depth + 1, out);
  }

  if (skipped > 0) {
    out.push(skippedMarker(skipped, depth));
  }
  return used;
};

/**
 * Serializes the visible DOM into the compact targeting tree. Structural
 * ancestors of interesting elements are kept so the tree reads as a page
 * layout; the fair-share budget keeps every region represented on large
 * pages. Output is capped (depth, node count, character budget) and a
 * truncated tree ends with explicit markers.
 */
export const serializePageDom = (
  root: Element = document.body,
  limits: SerializeLimits = DEFAULT_LIMITS,
): string => {
  if (!root) {
    return '';
  }
  const tree = buildTree(root, limits);
  const lines: string[] = [];
  renderTree(tree, limits.maxNodes, 0, lines);
  let output = lines.join('\n');
  if (output.length > limits.maxChars) {
    output = `${output.slice(0, limits.maxChars)}\n… (truncated)`;
  }
  return output;
};
